/* eslint-disable max-lines */
/* eslint-disable react/jsx-handler-names */
"use client";
import PropType from "prop-types";
import React, { useMemo, useRef, useState } from "react";
import { getPdpWidgetPincode, getProductVideoUrl } from "../../../utils/pdp/pdpWidgetUtils";
import { getLatLongFromSavedAddressSync } from "../../../store/sagas/helpers/savedAddressLatLong";
import ProductCard from "../productCard";
import Slider from "react-slick";
import { SlickNextArrow, SlickPrevArrow } from "../../molecules/carouselHelpers";
import { shallowEqual, useSelector } from "react-redux";
import { setGA4PdpPageGaEvent } from "../../../analytics/gaEvents";
import useAnalytics from "../../../analytics/useAnalytics";
import { GA4_EVENTS } from "../../../analytics/gaConstant";
import { PageTypes } from "../../../utils/fnpPageType";
import { VIEW_ALL } from "../../../constants/common/common";
import { debounce } from "../../../utils/common";
import { getDisplayTags } from "../../../utils/plp/common";
import useHorizontalScrollForCarousel from "../../../customHooks/horizontalScroll";
import CarouselScroller from "../../molecules/carouselScroller";
import useWidgetCarouselScroller from "./useWidgetCarouselScroller";
import useWidgetImpressions from "./useWidgetImpressions";
import useWidgetDeliveryDates from "./useWidgetDeliveryDates";

/**
 * Horizontal product carousel for widgets (home, microsite, etc.).
 *
 * @param {Object} props
 * @param {string} [props.widgetTitle]
 * @param {unknown} [props.productsList] - Product array; non-arrays (e.g. failed API) are treated as empty.
 * @returns {JSX.Element}
 */
const Widget = ({
  widgetTitle,
  productsList,
  isShowEarliestDate = true,
  isMobile,
  type = "",
  position = "1",
  skuCode,
  isArrowVisibileDesktop = true,
  isHideSavedPercent = false,
  pageType,
  titleStyle = "pl-16",
  promo = "",
  viewAll = "",
  isShowTitle = true,
  slidesToShow = 4,
  isFromSearch = false,
  isShowReview = true,
}) => {
  const safeProductsList = useMemo(() => (Array.isArray(productsList) ? productsList : []), [productsList]);

  const { trackEvent } = useAnalytics();
  const widgetRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const { onWidgetProductClick } = useWidgetImpressions(safeProductsList);
  const {
    mobileScrollRef,
    currentSlide,
    setCurrentSlide,
    totalSlides,
    showScroller,
    effectiveSlidesToShow,
    slidesToScroll,
  } = useWidgetCarouselScroller({
    isMobile,
    slidesToShow,
    totalItems: safeProductsList.length,
  });

  const isPdpWidget = pageType === PageTypes.PRODUCT;

  const {
    selectedCurrencyFromState,
    clientCurrentClickedItem,
    fullProductDetails,
    clientVariantLabel,
    pincodeFromState,
    selectedIntLocation,
    location,
    isUserLoggedIn,
    productsTagAttr,
    savedAddresses,
  } = useSelector(
    (state) => ({
      selectedCurrencyFromState: state?.currencyList?.userSelectedCurrency,
      clientCurrentClickedItem: state?.pdp?.clientCurrentClickedItem,
      fullProductDetails: state?.pdp?.fullProductDetails,
      clientVariantLabel: state?.pdp?.clientVariantLabel,
      pincodeFromState: state?.locationLock?.selectedDomesticLocation?.pincode,
      selectedIntLocation: state?.locationLock?.selectedIntLocation,
      location: state?.locationLock?.selectedIntLocation?.location,
      isUserLoggedIn: state?.fus?.loginDetail?.loggedin,
      productsTagAttr: state?.common?.productsTagAttr,
      savedAddresses: state?.savedAddress?.savedAddresses,
    }),
    shallowEqual,
  );

  const pincode = getPdpWidgetPincode({
    pincodeFromState,
    selectedIntLocation,
  });
  const latLong = useMemo(() => getLatLongFromSavedAddressSync(savedAddresses), [savedAddresses]);

  const { resolveDeliveryEntry, sectionRef } = useWidgetDeliveryDates({
    productsList: safeProductsList,
    pincode,
    isEnabled: isShowEarliestDate,
    latLong,
  });

  useHorizontalScrollForCarousel(widgetRef, isHovered, isMobile);

  /**
   * Handles the widget click event and sends a GA4 event with detailed product information.
   *
   * @function
   * @returns {void}
   */
  const onWidgetClicked = debounce(() => {
    onWidgetProductClick();
    const properties = setGA4PdpPageGaEvent(
      clientCurrentClickedItem,
      pincode,
      isUserLoggedIn,
      selectedCurrencyFromState?.currency?.id,
      fullProductDetails,
      clientVariantLabel,
      clientCurrentClickedItem?.catalogId,
      location,
      GA4_EVENTS?.WIDGET_VIEWED,
      type,
      position,
    );
    trackEvent(GA4_EVENTS?.WIDGET_VIEWED, { ...properties }, ["ga4"]);
  }, 100);

  /**
   * Genrate final Url if promo code
   */
  const generateFinalUrl = (url, promo, index) => {
    let finalUrl = url;
    if (promo && promo !== "" && url) {
      const [baseUrl] = url.split("?");
      finalUrl = `${baseUrl}?promo=${promo}_${index}`;
    }
    return finalUrl;
  };

  const desktopSettings = useMemo(
    () => ({
      dots: false,
      infinite: false,
      slidesToShow: slidesToShow,
      slidesToScroll: slidesToScroll,
      arrows: isArrowVisibileDesktop,
      pagination: false,
      autoplay: false,
      centerMode: false,
      nextArrow: <SlickNextArrow />,
      prevArrow: <SlickPrevArrow />,
      beforeChange: (_, next) => setCurrentSlide(next),
    }),
    [isArrowVisibileDesktop, setCurrentSlide, slidesToScroll, slidesToShow],
  );

  const listItemWidth = 100 / 2.3;

  /**Returns the padding for component */
  const returnPaddingForPageType = (pageType) => {
    switch (pageType) {
      case PageTypes.HOME:
        if (isFromSearch) {
          return;
        } else {
          return "px-16";
        }
      case PageTypes?.AUTOSUGGEST:
        return;
      case PageTypes.CATEGORY:
        return "";
      default:
        return "px-16 pt-24 md:pt-0";
    }
  };

  const baseTitleClasses = "overflow-hidden text-ellipsis pl-2 text-left";

  const pdpTitleClasses =
    "font-inter text-black-100 font-600 capitalize leading-20 text-16 md:text-24 md:leading-[32px] [font-variant-numeric:lining-nums_proportional-nums] [font-feature-settings:'dlig'_on]";

  const defaultTitleClasses = `text-16 font-600 capitalize ${isFromSearch ? "" : "md:text-24"}`;

  const titleClasses =
    pageType === PageTypes.PRODUCT
      ? `${baseTitleClasses} ${pdpTitleClasses}`
      : `${baseTitleClasses} ${defaultTitleClasses}`;

  return (
    <section
      ref={sectionRef}
      className={`productCarousel w-full md:px-0 ${returnPaddingForPageType(pageType)}`}
      role="region"
      aria-label="Product carousel"
      id={widgetTitle?.replace(/\s+/g, "")}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {isShowTitle && widgetTitle && safeProductsList.length > 0 && (
        <div className={`mb-12 flex w-full items-center justify-between text-left md:mb-16 md:mb-16 ${titleStyle}`}>
          <p className={titleClasses}>{widgetTitle}</p>
          {viewAll && viewAll !== "" && (
            <a
              href={viewAll}
              className="inline-flex w-[100%] max-w-[120px] items-center justify-center rounded-8 border-1 border-fnp bg-fnp-50 px-16 py-8 text-12 font-600 text-fnp-300 md:text-14"
            >
              {VIEW_ALL}
            </a>
          )}
        </div>
      )}
      {isMobile ? (
        <ul ref={mobileScrollRef} className="scrollbar-hide flex w-full gap-16 overflow-x-auto overflow-y-hidden">
          {safeProductsList.length > 0 &&
            safeProductsList.map((product, i) => {
              const productVideoUrl = getProductVideoUrl(product);
              const shouldAutoPlayVideo = Boolean(productVideoUrl);

              return (
                <li
                  className="flex-none pb-16"
                  key={i}
                  style={{ width: `${listItemWidth}%` }}
                  // eslint-disable-next-line react/jsx-handler-names
                  onClick={pageType === PageTypes?.PRODUCT ? onWidgetClicked : undefined}
                >
                  <ProductCard
                    image={product?.productImage}
                    productUrl={generateFinalUrl(product?.url || product?.productUrl, promo, i)}
                    title={product?.productName}
                    price={product?.price}
                    productId={product?.productId}
                    imgHeight={428}
                    imgWidth={428}
                    urlIdentifier={product?.urlIdentifier}
                    deliveryEntry={resolveDeliveryEntry(product)}
                    pincode={pincode}
                    review={isShowReview && (product?.review || product?.rating)}
                    isMobile={isMobile}
                    imageCount={Number(product?.imageCount)}
                    productWidget={product}
                    productCount={i}
                    skuCode={skuCode}
                    productAttributes={getDisplayTags(productsTagAttr, product?.["productAttributes"])}
                    isHideSavedPercent={isHideSavedPercent}
                    isFromSearch={isFromSearch}
                    widgetTitle={widgetTitle}
                    isAutoPlay={shouldAutoPlayVideo}
                    isLoop={shouldAutoPlayVideo}
                    isPlayVideoOnCardHover={isPdpWidget && !isMobile}
                  />
                </li>
              );
            })}
        </ul>
      ) : (
        <Slider {...desktopSettings} className="leftAlignSlick relative w-full" ref={widgetRef}>
          {safeProductsList.length > 0 &&
            safeProductsList.map((product, i) => (
              <div
                className="h-full px-12"
                key={i}
                // eslint-disable-next-line react/jsx-handler-names
                onClick={pageType === PageTypes?.PRODUCT ? onWidgetClicked : undefined}
              >
                <ProductCard
                  image={product?.productImage}
                  productUrl={generateFinalUrl(product?.url || product?.productUrl, promo, i)}
                  title={product?.productName}
                  price={product?.price}
                  productId={product?.productId}
                  imgHeight={428}
                  imgWidth={428}
                  urlIdentifier={product?.urlIdentifier}
                  deliveryEntry={resolveDeliveryEntry(product)}
                  pincode={pincode}
                  review={isShowReview && (product?.review || product?.rating)}
                  isMobile={isMobile}
                  imageCount={Number(product?.imageCount)}
                  productWidget={product}
                  productCount={safeProductsList?.length}
                  skuCode={skuCode}
                  isHideSavedPercent={isHideSavedPercent}
                  isSmallReviewTag={isMobile}
                  widgetTitle={widgetTitle}
                  productAttributes={getDisplayTags(productsTagAttr, product?.["productAttributes"])}
                  isFromSearch={isFromSearch}
                  isPlayVideoOnCardHover={isPdpWidget && !isMobile}
                />
              </div>
            ))}
        </Slider>
      )}
      {isPdpWidget && showScroller && (
        <CarouselScroller
          currentSlide={currentSlide}
          totalSlides={totalSlides}
          slidesToShow={effectiveSlidesToShow}
          totalItems={safeProductsList.length}
          slidesToScroll={slidesToScroll}
          isPdpWidget={true}
          isMobile={isMobile}
        />
      )}
    </section>
  );
};

Widget.propTypes = {
  widgetTitle: PropType.string,
  productsList: PropType.oneOfType([PropType.array, PropType.object]),
  isShowEarliestDate: PropType.bool,
  isMobile: PropType.bool,
  isArrowVisibileDesktop: PropType.bool,
  type: PropType.string,
  position: PropType.number,
  skuCode: PropType.string,
  isHideSavedPercent: PropType.bool,
  pageType: PropType.string.isRequired,
  titleStyle: PropType.string,
  promo: PropType.string,
  viewAll: PropType.string,
  isShowTitle: PropType.bool,
  isFromSearch: PropType.bool,
  slidesToShow: PropType.number,
  isShowReview: PropType.bool,
  isShowRatingWhenAvailable: PropType.bool,
};

export default React.memo(Widget);
